import { getFeatures, isModelsAvailable } from '../../../../utils/modulesFeatures';
import { useAngularEventOn } from '../../../../hooks/useAngularEventOn';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// Utils
import { angularEmit } from '../../../../utils';
import { CreateItemForm } from '../components/CreateItemForm';
import { Item } from '../components/Item';

// Components
import { MenuContainer } from '../components/MenuContainer';
import { TypeInfo } from '../components/TypeInfo';
import { useAdjustPosition } from '../hooks/useAdjustPosition';
import { flowItemsDescription, flowItemTitle, flowItemTypeEnum } from '../utils';

// State
import { useAppState } from './useCreateFlowItemMenuAppState';
import { CreateCustomComponentItemForm } from '../components/CreateCustomComponentItemForm';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';

const ItemsWrapperStyle = styled.div`
    &&& {
        display: flex;
        margin-bottom: 8px;
        justify-content: space-between;
    }
`;

const isRPAAvailable = () => {
    try {
        return getFeatures().RPA;
    } catch (e) {
        return false;
    }
};

export const CreateFlowItemMenu = () => {
    const { state, actions, selectors } = useAppState();
    const getNameForNewItemRef = useRef<any>((name: string) => name);
    const { top, left, isVisible, selectedItemType, initialFlowItem } = state;

    const types = Object.values<(typeof flowItemTypeEnum)[keyof typeof flowItemTypeEnum]>(flowItemTypeEnum)
        .filter((item) => item !== flowItemTypeEnum.PROCESS_CUSTOM_COMPONENTS)
        .filter(
            (item) =>
                isRPAAvailable() || ![flowItemTypeEnum.PROCESS_RPA, flowItemTypeEnum.PROCESS_RPA_START].includes(item),
        )
        .filter((item) => isModelsAvailable() || ![flowItemTypeEnum.PROCESS_INVOKE_MODEL_INSTANCE].includes(item));

    const changeItemTypeFn = (itemType: (typeof flowItemTypeEnum)[keyof typeof flowItemTypeEnum]) =>
        actions.setItemType({ itemType });
    const submitFormFn = (data: { name: string; description: string; isNeedSetMapping?: boolean }) => {
        actions.hide();
        actions.addCreatedComponentName(data.name);

        const emitData = {
            name: data.name,
            description: data.description,
            type: selectedItemType,
            flowItemId: (initialFlowItem as any)?.item?.Id,
            // @ts-ignore
            customComponentItem: cloneDeep(initialFlowItem.item),
            isNeedSetMapping: data.isNeedSetMapping,
        };

        angularEmit('react:change-item-metadata', emitData);
    };

    useAngularEventOn(
        'react:show-create-task-item-menu',
        (
            e: any,
            data: {
                event: { offsetY: number; offsetX: number };
                flowItem: any;
                isCustomComponent: boolean;
                getNameForNewItem: (name: string, flowItem: any) => string;
            },
        ) => {
            getNameForNewItemRef.current = (name: string) => data.getNameForNewItem(name, data.flowItem);
            actions.hide();
            actions.show({
                top: data.event.offsetY,
                left: data.event.offsetX,
                initialFlowItem: data.flowItem,
                selectedItemType: data.isCustomComponent
                    ? flowItemTypeEnum.PROCESS_CUSTOM_COMPONENTS
                    : selectedItemType,
            });
        },
    );

    useAdjustPopupPosition(isVisible, top, left);

    const initialName = (initialFlowItem as any)?.item?.Name;

    useEffect(() => {
        if (!(isVisible && initialName?.length > 0)) {
            return;
        }
        actions.addCreatedComponentName(initialName);
        // eslint-disable-next-line
    }, [isVisible, initialName]);

    if (isVisible) {
        return ReactDOM.createPortal(
            <OutsideClickHandler onOutsideClick={actions.hide}>
                <MenuContainer top={top} left={left}>
                    {selectedItemType !== flowItemTypeEnum.PROCESS_CUSTOM_COMPONENTS && (
                        <ItemsWrapperStyle>
                            {types.map((type, i, array) => {
                                const disableSeparator = i === array.length - 1;
                                return (
                                    <Item
                                        key={type}
                                        type={type}
                                        onSelect={changeItemTypeFn}
                                        selected={selectedItemType === type}
                                        disableSeparator={disableSeparator}
                                    />
                                );
                            })}
                        </ItemsWrapperStyle>
                    )}

                    {selectedItemType === flowItemTypeEnum.PROCESS_CUSTOM_COMPONENTS && (
                        <CreateCustomComponentItemForm
                            initialValues={{
                                // @ts-ignore
                                name: initialName,
                                description: '',
                            }}
                            onSubmit={submitFormFn}
                            onHandleBack={actions.hide}
                            RenderInfo={() => (
                                <TypeInfo
                                    typeHead="Component"
                                    // @ts-ignore
                                    name={initialFlowItem.item.DisplayName}
                                    // @ts-ignore
                                    description={initialFlowItem.item.Description || 'none'}
                                />
                            )}
                        />
                    )}

                    {selectedItemType !== flowItemTypeEnum.PROCESS_CUSTOM_COMPONENTS && (
                        <CreateItemForm
                            initialValues={{
                                // @ts-ignore
                                name: initialName,
                                description: '',
                            }}
                            onSubmit={submitFormFn}
                            selectedItemType={selectedItemType}
                            getNameForNewItem={getNameForNewItemRef.current}
                            RenderInfo={() => (
                                <TypeInfo
                                    typeHead="Type"
                                    // @ts-ignore
                                    name={flowItemTitle[state.selectedItemType] || ''}
                                    // @ts-ignore
                                    description={flowItemsDescription[state.selectedItemType]}
                                />
                            )}
                        />
                    )}
                </MenuContainer>
            </OutsideClickHandler>,
            document.querySelector('.de-editor-workflow-canvas-wrap') as Element,
        );
    }

    return null;
};

function useAdjustPopupPosition(isVisible: boolean, top: number, left: number) {
    const editorSelector = '.de-editor-workflow-canvas-wrap';

    const [editorPaddings, setEditorPaddings] = useState<any>();

    useEffect(() => {
        if (!isVisible || editorPaddings) {
            return;
        }

        const editor = document.querySelector(editorSelector) as Element;
        const editorHeight = editor.clientHeight;

        const validationControls = document.querySelector<HTMLElement>(editorSelector + ' .validation-workflow');

        const top = (validationControls?.offsetTop || 0) + (validationControls?.offsetHeight || 0);
        const bottom = Math.max(
            ...['.workflow-edit-controls', '.workflow-edit-zoom-wrapper'].map(
                (s) => editorHeight - (document.querySelector<HTMLElement>(s)?.offsetTop || editorHeight) + 10,
            ),
        );

        setEditorPaddings({ top, bottom });
        // eslint-disable-next-line
    }, [isVisible && !editorPaddings]);

    useAdjustPosition('.create-flow-item-menu', editorSelector, [top, left, editorPaddings], editorPaddings);
}
